import {
  Box,
  Button,
  Divider,
  Grid2,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { t } from "i18next";
import { RemoteCompany } from "../../../hooks/Remote/Companies/useRemoteFetchCompanies";
import { CompanyUnits } from "../../../hooks/Remote/CompanyUnits/useRemoteFetchCompanyUnits";
import { Colleague } from "../../../features/Connections/types/Colleague.type";
import { FileObject } from "../../../features/FileDropzone/typings/FileObject.type";
import { useMemo, useState } from "react";
import { Delete } from "@mui/icons-material";
import { PictureDropzone2 } from "../../Pickers/picture-dropzone.component(2)";
import { ItemPermissionSelect } from "./List-Permission-Select.component";
import { normalizePicturePaths } from "../../BookingForm/functions/normalizePicturePaths.function";
import { EnlargedImageCarousel } from "../../../features/EnlargedImageCarousel/enlarged-image-carousel.component";

/** display simple cell */
export const renderSimpleCellName = (name?: string) => {
  return <>{name ?? ""}</>;
};

/** display no image */
export const NoItemImage = ({ height }: { height: number }) => (
  <Skeleton variant={"rounded"} height={height} width={height} animation={false}>
    <Box width={height} height={height} sx={{ textAlign: "center", alignContent: "center" }}>
      <Typography variant={"body2"} sx={{ visibility: "visible" }}>
        {t("No Image")}
      </Typography>
    </Box>
  </Skeleton>
);

/** display permission cell */
export const renderPermissionCell = (
  permRow: {
    affectedCompanies: string[];
    affectedDepartments: string[];
    affectedUsers: string[];
  },
  remoteCompanies: RemoteCompany[] | undefined,
  remoteDepartments: CompanyUnits[] | undefined,
  remoteAllUsers: Colleague[] | undefined
) => {
  const arrAffectedCompanies = permRow.affectedCompanies ?? [];
  const arrAffectedDepartments = permRow.affectedDepartments ?? [];
  const arrAffectedUsers = permRow.affectedUsers ?? [];

  const arrCompanies = remoteCompanies
    ?.filter(comp => arrAffectedCompanies.includes(String(comp.id)))
    .map(entry => ({ id: entry.id, name: entry.name }));
  const arrDepartments = remoteDepartments
    ?.filter(depart => arrAffectedDepartments.includes(depart.id))
    .map(entry => ({ id: entry.id, name: entry.name }));
  const arrUsers = remoteAllUsers
    ?.filter(user => arrAffectedUsers.includes(user.userId))
    .map(entry => ({ id: entry.userId, name: `${entry.firstName} ${entry.surname}` }));

  const perms = [...(arrCompanies ?? []), ...(arrDepartments ?? []), ...(arrUsers ?? [])].map(
    p => p.name
  );

  return (
    <>
      {perms.length > 3 ? (
        <>
          {perms.slice(0, 3).join(", ")}
          {t("_permissionsMore", { number: perms.length - 3 })}
        </>
      ) : (
        <>
          {perms
            .slice(0, 3)
            .join(", ")
            .replace(/,([^,]*)$/, ` ${t("and")}$1`)}
        </>
      )}
    </>
  );
};

/** edit permission select */
export const editPermissionSelect = ({
  permRow,
  remoteCompanies,
  remoteDepartments,
  remoteAllUsers
}: {
  permRow: {
    affectedCompanies: string[];
    affectedDepartments: string[];
    affectedUsers: string[];
  };
  remoteCompanies: RemoteCompany[] | undefined;
  remoteDepartments: CompanyUnits[] | undefined;
  remoteAllUsers: Colleague[] | undefined;
}) => {
  return (
    <Box sx={{ mt: 1, width: "100%" }}>
      <Typography variant={"subtitle1"} sx={{ fontWeight: "bolder" }}>
        {t("Permissions")}
      </Typography>
      <Typography sx={{ mb: 1 }}>{t("_ManagementPermissionDesc")}</Typography>
      <ItemPermissionSelect
        permRow={permRow}
        remoteCompanies={remoteCompanies}
        remoteDepartments={remoteDepartments}
        remoteAllUsers={remoteAllUsers}
      />
    </Box>
  );
};

/** display Table Item Image */
export const renderTableItemImage = (
  picturePath: string | null | undefined,
  imageAlt: string,
  height: number,
  setShowBiggerImage?: (image: string) => void
) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
      {picturePath && (
        <Button
          sx={{ p: 0 }}
          variant={"text"}
          disabled={!setShowBiggerImage}
          onClick={() => setShowBiggerImage?.(picturePath)}
          data-testid="show-bigger-image-btn"
        >
          <img
            height={height}
            width={height}
            style={{ borderRadius: "8px" }}
            src={picturePath} // ?? `https://picsum.photos/${height}/${height}`}
            loading="lazy"
            alt={imageAlt}
          />
        </Button>
      )}

      {/* if image url doesn't exist */}
      {!picturePath && <NoItemImage height={height} />}
    </Box>
  );
};

/** edit component for the item image */
export const EditTableItemImageComp = ({
  title,
  header,
  rowOriginal,
  imageFile,
  setImageFile,
  setDeleteRowImage
}: Readonly<{
  title: string;
  header: string;
  rowOriginal: { id: number; picturePath: string | null | undefined };
  imageFile: { file: FileObject[] };
  setImageFile: (newImageFile: { file: FileObject[] }) => void;
  setDeleteRowImage: (item: { rowId: number | null; picturePath: string | null }) => void;
}>) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const editImageHeight = isMobile ? 150 : 250;

  const [showCurrentImage, setShowCurrentImage] = useState(!!rowOriginal.picturePath);

  const removeCurrentImage = () => {
    setShowCurrentImage(false);
    setDeleteRowImage({ rowId: rowOriginal.id, picturePath: null });
  };

  return (
    <Grid2 container data-testid={`${title}-list-edit-image-container`}>
      <Grid2 size={{ xs: 12, sm: 6 }} sx={{ pr: 2 }}>
        <Typography sx={{ mb: 2 }}>{header}</Typography>
        {showCurrentImage ? (
          renderTableItemImage(rowOriginal.picturePath, "item", editImageHeight)
        ) : (
          <NoItemImage height={editImageHeight} />
        )}
        <Divider sx={{ mt: 2, mb: 2, maxWidth: editImageHeight }} />
        {rowOriginal.picturePath && (
          <Button data-testid="delete-current-image-btn" onClick={removeCurrentImage}>
            <Delete fontSize="small" sx={{ mr: 0.5 }} />
            <Typography sx={{ fontSize: "0.9rem" }}>{t("Delete picture")}</Typography>
          </Button>
        )}
      </Grid2>

      <Grid2 size={{ xs: 12, sm: 6 }}>
        <PictureDropzone2
          title={title}
          files={imageFile.file}
          error={!imageFile.file}
          onAdd={files => setImageFile({ file: files })}
          onDelete={() => setImageFile({ file: [] })}
        />
      </Grid2>
    </Grid2>
  );
};

/** display Table Item MULTI Images */
export const renderTableItemMultiImages = (
  picturePaths: (string | null | undefined)[] | null,
  imageAlt: string,
  height: number,
  setShowBiggerImages?: (image: string[]) => void
) => {
  const normPicturePaths = normalizePicturePaths(picturePaths);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
      {normPicturePaths !== null && (
        <Button
          sx={{ p: 0 }}
          variant={"text"}
          disabled={!setShowBiggerImages}
          onClick={() => setShowBiggerImages?.(normPicturePaths)}
          data-testid="show-bigger-image-btn"
        >
          <img
            height={height}
            width={height}
            style={{ borderRadius: "8px" }}
            // show first image of picturePaths
            src={normPicturePaths.at(-1)} // ?? `https://picsum.photos/${height}/${height}`}
            loading="lazy"
            alt={imageAlt}
          />
        </Button>
      )}

      {/* if image url doesn't exist */}
      {!normPicturePaths && <NoItemImage height={height} />}
    </Box>
  );
};

export const editTableItemMultiImagesComp = ({
  title,
  rowOriginal,
  imageFiles,
  setImageFiles,
  handleDelete
}: Readonly<{
  title: string;
  rowOriginal: { id: number | string; picturePaths: (string | null | undefined)[] | null };
  imageFiles: FileObject[];
  setImageFiles: React.Dispatch<React.SetStateAction<FileObject[]>>;
  handleDelete: (picturePath: string) => void;
}>) => {
  return (
    <EditTableItemMultiImagesComp
      title={title}
      rowOriginal={rowOriginal}
      imageFiles={imageFiles}
      setImageFiles={setImageFiles}
      handleDelete={handleDelete}
    />
  );
};

/** edit component for multiple item images */
export const EditTableItemMultiImagesComp = ({
  title,
  rowOriginal,
  imageFiles,
  setImageFiles,
  handleDelete
}: {
  title: string;
  rowOriginal: { id: number | string; picturePaths: (string | null | undefined)[] | null };
  imageFiles: FileObject[];
  setImageFiles: React.Dispatch<React.SetStateAction<FileObject[]>>;
  handleDelete: (picturePath: string) => void;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Memoize so the same array reference is passed when paths haven't changed.
  // The fleet table edit cell re-renders often; without this, normalizePicturePaths()
  // returns a new array every time and EnlargedImageCarousel re-runs its preload → flicker.
  const normPicturePaths = useMemo(
    () => normalizePicturePaths(rowOriginal.picturePaths),
    [JSON.stringify(rowOriginal.picturePaths ?? [])]
  );

  return (
    <Grid2 container sx={{ width: "100%" }} data-testid={`${title}-list-edit-image-container`}>
      <Box
        sx={[
          { alignContent: "center" },
          isMobile ? { width: "100%", mb: 2 } : { flex: 2, pr: 2, minWidth: 0 }
        ]}
      >
        <EnlargedImageCarousel
          picturePaths={normPicturePaths}
          imageSize={30}
          boxSize={30}
          handleDelete={handleDelete}
          isLoading={false}
        />
      </Box>

      <Box sx={isMobile ? { width: "100%" } : { flex: 1 }}>
        <PictureDropzone2
          title={title}
          maxFiles={5}
          files={imageFiles}
          onAdd={newFiles => {
            if (newFiles.length) {
              setImageFiles(prev => [...prev, ...newFiles]);
            }
          }}
          onDelete={(_, index) => setImageFiles(prev => prev.filter((_, i) => i !== index))}
        />
      </Box>
    </Grid2>
  );
};
